import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import DataGrid from '../ui/DataGrid';
import DataCard from '../ui/DataCard';

/**
 * DashboardOverviewTab - Übersicht Tab im Dashboard
 * 
 * Zeigt:
 * - Aktive Sessions (hervorgehoben)
 * - Letzte Aktivität (Kurse, Vorlagen)
 * - Schnellaktionen
 */
export default function DashboardOverviewTab({
  courses,
  templates,
  activeSessions,
  allInstances,
  onStartQuiz,
  onEndSession,
  onDeleteSession,
  onCreateCourse,
  onCreateTemplate,
  onOpenAIGenerator
}) {
  const navigate = useNavigate();

  // Get recent templates (sorted by lastUsed or createdAt)
  const recentTemplates = [...templates]
    .sort((a, b) => (b.lastUsed || b.createdAt) - (a.lastUsed || a.createdAt))
    .slice(0, 6);

  // Get template count per course
  const getTemplateCount = (courseId) => {
    return templates.filter(t => t.courseId === courseId).length;
  };

  // Get course name by ID
  const getCourseName = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course?.name || 'Ohne Kurs';
  };

  return (
    <div className="space-y-8">
      {/* Active Sessions Section */}
      {activeSessions.length > 0 && (
        <section>
          <h2 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <span className="w-2 h-2 bg-danger-500 rounded-full animate-pulse" />
            Aktive Sessions
          </h2>
          <div className="space-y-2">
            {activeSessions.map((session) => (
              <DataCard
                key={session.id}
                variant="compact"
                color="#EF4444"
                title={session.templateName || 'Quiz Session'}
                subtitle={`Code: ${session.sessionCode} • ${session.participants?.length || 0} Teilnehmer`}
                badges={[{ label: 'Live', variant: 'danger', pulse: true }]}
                menuActions={[
                  { label: 'Öffnen', onClick: () => navigate(`/live/${session.id}`) },
                  { label: 'Ergebnisse', onClick: () => navigate(`/results/${session.id}`) },
                  { label: 'Beenden', variant: 'warning', onClick: () => onEndSession(session.id) },
                  { label: 'Löschen', variant: 'danger', onClick: () => onDeleteSession(session.id) }
                ]}
                onClick={() => navigate(`/live/${session.id}`)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Quick Actions */}
      <section>
        <h2 className="text-base font-medium text-gray-900 dark:text-white mb-4">
          Schnellaktionen
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <button
            onClick={onCreateTemplate}
            className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-500 hover:shadow-md transition-all text-left"
          >
            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
              <span className="text-primary-600 dark:text-primary-400 font-bold">+</span>
            </div>
            <div>
              <div className="font-medium text-gray-900 dark:text-white">Neue Vorlage</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Quiz manuell erstellen</div>
            </div>
          </button>
          
          <button
            onClick={onOpenAIGenerator}
            className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-secondary-500 hover:shadow-md transition-all text-left"
          >
            <div className="w-10 h-10 bg-secondary-100 dark:bg-secondary-900/30 rounded-lg flex items-center justify-center">
              <span className="text-secondary-600 dark:text-secondary-400 font-bold">KI</span>
            </div>
            <div>
              <div className="font-medium text-gray-900 dark:text-white">KI Generator</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Mit KI generieren</div>
            </div>
          </button>
          
          <button
            onClick={onCreateCourse}
            className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-success-500 hover:shadow-md transition-all text-left"
          >
            <div className="w-10 h-10 bg-success-100 dark:bg-success-900/30 rounded-lg flex items-center justify-center">
              <span className="text-success-600 dark:text-success-400 font-bold">+</span>
            </div>
            <div>
              <div className="font-medium text-gray-900 dark:text-white">Neuer Kurs</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Vorlagen organisieren</div>
            </div>
          </button>
        </div>
      </section>

      {/* Recent Templates */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-medium text-gray-900 dark:text-white">
            Kürzliche Vorlagen
          </h2>
          <button
            onClick={() => navigate('/templates')}
            className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            Alle anzeigen →
          </button>
        </div>
        
        <DataGrid
          items={recentTemplates}
          columns={{ sm: 1, md: 2, lg: 3 }}
          emptyState={{
            title: 'Noch keine Vorlagen',
            description: 'Erstelle deine erste Quizvorlage',
            actions: [
              { label: 'Vorlage erstellen', onClick: onCreateTemplate },
              { label: 'Mit KI generieren', onClick: onOpenAIGenerator, variant: 'secondary' }
            ]
          }}
          renderItem={(template) => (
            <DataCard
              key={template.id}
              variant="compact"
              title={template.name}
              subtitle={`${getCourseName(template.courseId)} • ${template.questions?.length || 0} Fragen`}
              onClick={() => navigate(`/template/${template.id}`)}
              menuActions={[
                { label: 'Bearbeiten', onClick: () => navigate(`/template/${template.id}`) },
                { label: 'Quiz starten', onClick: () => onStartQuiz(template.id) }
              ]}
            />
          )}
        />
      </section>

      {/* Courses Overview */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-medium text-gray-900 dark:text-white">
            Deine Kurse
          </h2>
          <button
            onClick={onCreateCourse}
            className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            + Neuer Kurs
          </button>
        </div>
        
        <DataGrid
          items={courses.slice(0, 4)}
          columns={{ sm: 1, md: 2, lg: 2 }}
          emptyState={{
            title: 'Noch keine Kurse',
            description: 'Erstelle deinen ersten Kurs um Vorlagen zu organisieren',
            action: { label: 'Kurs erstellen', onClick: onCreateCourse }
          }}
          renderItem={(course) => (
            <DataCard
              key={course.id}
              icon={course.icon}
              color={course.color}
              title={course.name}
              subtitle={course.description}
              stats={[
                { value: getTemplateCount(course.id), label: 'Vorlagen' }
              ]}
              primaryAction={{
                label: 'Öffnen',
                onClick: () => navigate(`/course/${course.id}`)
              }}
              onClick={() => navigate(`/course/${course.id}`)}
            />
          )}
        />
      </section>
    </div>
  );
}

DashboardOverviewTab.propTypes = {
  courses: PropTypes.array.isRequired,
  templates: PropTypes.array.isRequired,
  activeSessions: PropTypes.array.isRequired,
  allInstances: PropTypes.array.isRequired,
  onStartQuiz: PropTypes.func.isRequired,
  onEndSession: PropTypes.func.isRequired,
  onDeleteSession: PropTypes.func.isRequired,
  onCreateCourse: PropTypes.func.isRequired,
  onCreateTemplate: PropTypes.func.isRequired,
  onOpenAIGenerator: PropTypes.func.isRequired
};
